// LogosPanel with custom hook
import * as React from 'react';
import {
  LoadingOutlined,
  ReloadOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { Upload, Spin } from 'antd';
import DesignModel from '../../../Design/models/DesignModel';
import LogoImage from '../../../Design/view/LogoImage';
import { useTranslation } from 'react-i18next';
import AntdCustomButton from '../../../../components/buttons/AntdCustomButton';
import { SmallText } from '../../../../components/ui/Typography';
import ImageWebsiteLogo from '../../../Design/view/files/ImageWebsiteLogo';
import useLogoUpload from '../../../../hooks/useLogoUpload';
import { observer } from 'mobx-react-lite';
import IframeServiceInjector from '../../services/injectors/IframeServiceInjector';
import FrameEventMessanger from '../../services/frames/FrameEventMessanger';
import { CHANGE_SITE_LOGO } from '../../consts/events';

interface Props {
  onClose?: () => void;
}

const LogosPanel: React.FC<Props> = observer(({ onClose }) => {
  const { t } = useTranslation();

  const handleSelect = (logo: ImageWebsiteLogo | null) => {
    if (logo) {
      DesignModel.changeSelectedLogo?.(logo);
      IframeServiceInjector.getService<FrameEventMessanger>(
        'FrameEventMessanger'
      ).sendMessage(CHANGE_SITE_LOGO, {
        logoUrl: logo.getImageSrc()
      });
    } else {
      DesignModel.changeSelectedLogo?.(null);
      IframeServiceInjector.getService<FrameEventMessanger>(
        'FrameEventMessanger'
      ).sendMessage(CHANGE_SITE_LOGO, {
        logoUrl: null
      });
    }
  };
  const { isUploading, uploadProps } = useLogoUpload({
    onSuccess: (logo) => {
      setVisibleCount(Number.MAX_SAFE_INTEGER);
      handleSelect(logo);
    },
    onError: (error) => {
      console.error('Upload failed:', error);
    }
  });

  const hasReorderedInitially = React.useRef(false);
  let availablePool = [
    ...(DesignModel.foundLogos || []),
    ...(DesignModel.uploadedLogos || [])
  ];

  if (DesignModel.selectedLogo && !hasReorderedInitially.current) {
    const selectedLogoIndex = availablePool.findIndex(
      (logo) => logo.getId() === DesignModel.selectedLogo.getId()
    );
    if (selectedLogoIndex > 0) {
      const poolCopy = [...availablePool];
      const [selectedLogo] = poolCopy.splice(selectedLogoIndex, 1);
      poolCopy.unshift(selectedLogo);
      availablePool = poolCopy;
    }
    hasReorderedInitially.current = true;
  }

  const [visibleCount, setVisibleCount] = React.useState<number>(5);
  React.useEffect(() => {
    setVisibleCount((curr) => {
      if (availablePool.length === 0) return 0;

      const minVisible = Math.min(5, availablePool.length);

      return Math.min(Math.max(curr, minVisible), availablePool.length);
    });
  }, [availablePool.length]);

  const logos = availablePool.slice(0, visibleCount);

  const hiddenInPool = Math.max(availablePool.length - visibleCount, 0);
  const remainingScraped = Math.max(
    (DesignModel.allFoundLogos?.length || 0) -
      (DesignModel.foundLogosVisibleCount || 0),
    0
  );
  const totalRemaining = hiddenInPool + remainingScraped;
  const canLoadMore = totalRemaining > 0;

  return (
    <div>
      <div>
        <div className="border border-solid border-[rgba(0,0,0,0.15)] rounded-[12px] px-[16px] py-[24px] relative">
          <SmallText className="font-bold absolute top-[-16px] bg-white py-[4px] px-[16px] border border-solid border-[rgba(0,0,0,0.15)] rounded-full">
            {t('Logos')}
          </SmallText>

          <div
            className="flex flex-wrap gap-3 justify-between mb-[24px] overflow-y-auto"
            style={{
              maxHeight: logos.length > 10 ? '700px' : 'auto',
              overflowY: logos.length > 10 ? 'auto' : 'visible'
            }}>
            {logos.map((logo: any) => {
              const id = logo.getId ? logo.getId() : Math.random();
              const isSelected =
                DesignModel.selectedLogo &&
                logo.getId &&
                DesignModel.selectedLogo.getId &&
                DesignModel.selectedLogo.getId() === logo.getId();

              return (
                <div
                  className="p-[8px] rounded-[12px] mb-[10px] last:mb-0"
                  key={id}
                  onClick={() => handleSelect(logo)}
                  style={{
                    border: isSelected
                      ? '3px solid #3498DB'
                      : '1px dashed rgba(0,0,0,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 'calc(50% - 10px)',
                    cursor: 'pointer',
                    background: '#fff',
                    height: '129px'
                  }}>
                  <LogoImage
                    variant="panel"
                    logo={logo}
                    selected={isSelected}
                    onSelect={(l: any) => handleSelect(l)}
                  />
                </div>
              );
            })}
          </div>

          {canLoadMore && (
            <AntdCustomButton
              icon={<ReloadOutlined />}
              onClick={() => {
                if (hiddenInPool > 0) {
                  setVisibleCount(curr => Math.min(curr + 5, availablePool.length));
                } else if (remainingScraped > 0) {
                  DesignModel.loadMoreLogos?.(Math.min(5, remainingScraped));
                  setVisibleCount(curr => curr + 5);
                }
              }}
              className="flex items-center gap-2 w-full">
              {t('Load more logos')} ({totalRemaining})
            </AntdCustomButton>
          )}
        </div>

        <div className="mt-4">
          <Upload className="w-full" {...uploadProps} style={{ width: '100%' }}>
            <AntdCustomButton
              icon={
                isUploading ? (
                  <Spin
                    indicator={
                      <LoadingOutlined style={{ fontSize: 18 }} spin />
                    }
                  />
                ) : (
                  <UploadOutlined />
                )
              }
              className="flex items-center gap-2 w-full"
              disabled={isUploading}>
              {isUploading ? t('Uploading...') : t('Upload logo')}
            </AntdCustomButton>
          </Upload>
        </div>
      </div>
    </div>
  );
});

export default LogosPanel;